import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withDashboardLayout } from 'layouts/DashboardLayout/DashboardLayout';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useAlert } from 'context/AlertProvider';
import {
  Button,
  Card,
  Confirm,
  Dimmer,
  Icon,
  Loader,
  Message,
} from 'semantic-ui-react';
import PageContainer from 'components/PageContainer/PageContainer';
import {
  getMessageFromAxiosError,
  getMessageFromKey,
} from 'modules/message-factory/message-factory';
import WarehouseOrderPreparation from 'components/WarehouseOrderPreparation/WarehouseOrderPreparation';
import {
  canPrintLabels,
  isSFPStock,
  isStock,
} from 'modules/user-has-permission/user-has-permission';
import CriticalConfirmModal from 'components/Modals/CriticalConfirmModal/CriticalConfirmModal';
import { useGenerateManifest } from 'hooks/useGenerateManifest';
import PendingManifestKPI from 'components/PendingManifestKPI/PendingManifestKPI';
import { REDIRECTABLE_ERROR_TYPES } from 'constants/constants';
import {
  getCounter,
  pick,
  setAsClosed,
  setAsPicking,
  setAsTransfer,
} from 'services/preparation-orders.service';
import ArticlesReplacerModal from 'components/ArticlesReplacerModal/ArticlesReplacerModal';

function PreparationOrderPage() {
  const history = useHistory();
  const [preparationOrder, setPreparationOrder] = useState(null);
  const [isPrinting, setIsPrinting] = useState({
    message: 'Imprimiendo…',
    isActive: false,
  });
  const [loading, setLoading] = useState(false);
  const { reference } = useParams();
  const [isClosingPreparationOrder, setIsClosingPreparationOrder] =
    useState(false);
  const [isTransferPreparationOrder, setIsTransferPreparationOrder] =
    useState(false);
  const [isPickingPreparationOrder, setIsPickingPreparationOrder] =
    useState(false);
  const [confirmCloseIsVisible, setConfirmCloseIsVisible] = useState(false);
  const [confirmPickingIsVisible, setConfirmPickingIsVisible] = useState(false);
  const [confirmTransferIsVisible, setConfirmTransferIsVisible] =
    useState(false);
  const alert = useAlert();
  const userIsStock = useMemo(() => isStock(), []);
  const userIsSFPStock = useMemo(() => isSFPStock(), []);
  const userCanPrint = useMemo(() => canPrintLabels(), []);
  const { handleDownloadManifest, requestingManifestDownload } =
    useGenerateManifest();
  const palletInProgress = useMemo(
    () => preparationOrder?.not_manifested?.some((agency) => !!agency.count),
    [preparationOrder],
  );

  const checkAndRedirect = useCallback(
    (error) => {
      const errorType = error?.response?.data?.detail?.type;
      if (!errorType) return;
      if (REDIRECTABLE_ERROR_TYPES.includes(errorType)) {
        history.push('/preparation-orders');
      }
    },
    [history],
  );

  const pickPreparationOrder = useCallback(
    async (reference) => {
      try {
        setLoading(true);
        const { data: pickPreparationOrder } = await pick(reference);
        return pickPreparationOrder;
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error), () =>
          checkAndRedirect(error),
        );
      } finally {
        setLoading(false);
      }
    },
    [alert, checkAndRedirect],
  );

  const mergePreparationOrder = useCallback(
    async (preparationOrder) => {
      try {
        const { data: preparationOrderLabels } = await getCounter(reference);
        const updatedPreparationOrder = {
          ...preparationOrder,
          ...preparationOrderLabels,
        };
        setPreparationOrder(updatedPreparationOrder);

        return updatedPreparationOrder;
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      }
    },
    [alert, reference],
  );

  const handleConfirmClose = useCallback(async () => {
    try {
      setConfirmCloseIsVisible(false);
      setIsClosingPreparationOrder(true);
      await setAsClosed(reference);
      alert.current.message(
        getMessageFromKey('preparation_order.set_as_closed'),
        () => {
          setTimeout(() => {
            setIsClosingPreparationOrder(false);
            history.push('/preparation-orders');
          }, 4000);
        },
      );
    } catch (error) {
      alert.current.error(getMessageFromAxiosError(error), () => {
        setIsClosingPreparationOrder(false);
      });
    }
  }, [history, reference, alert]);

  const handleConfirmTransfer = useCallback(async () => {
    try {
      setConfirmTransferIsVisible(false);
      setIsTransferPreparationOrder(true);
      await setAsTransfer(reference);
      alert.current.message(
        getMessageFromKey('preparation_order.set_as_transfer'),
        () => {
          history.push('/preparation-orders');
        },
      );
    } catch (error) {
      alert.current.error(getMessageFromAxiosError(error));
    } finally {
      setIsTransferPreparationOrder(false);
    }
  }, [history, reference, alert]);

  const handleConfirmPicking = useCallback(async () => {
    try {
      setConfirmPickingIsVisible(false);
      setIsPickingPreparationOrder(true);
      await setAsPicking(reference);
      alert.current.message(
        getMessageFromKey('preparation_order.set_as_picking'),
        () => {
          history.push('/preparation-orders');
        },
      );
    } catch (error) {
      alert.current.error(getMessageFromAxiosError(error));
    } finally {
      setIsPickingPreparationOrder(false);
    }
  }, [history, reference, alert]);

  const updatePreparationOrder = useCallback(
    async (downloadManifest = false) => {
      try {
        if (downloadManifest) {
          await handleDownloadManifest(
            preparationOrder.agency.agency_code,
            () => mergePreparationOrder(preparationOrder),
          );
        }

        const { labels } = await mergePreparationOrder(preparationOrder);

        if (!labels?.length) {
          try {
            setIsClosingPreparationOrder(true);
            await setAsClosed(reference);
            alert.current.message(
              `La orden de preparación <strong>${reference}</strong> ha sido cerrada automáticamente debido a que no quedan etiquetas pendientes de imprimir`,
              () => {
                history.push('/preparation-orders');
              },
            );
          } catch (error) {
            alert.current.error(getMessageFromAxiosError(error));
          } finally {
            setIsClosingPreparationOrder(false);
          }
        }
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      }
    },
    [
      history,
      alert,
      preparationOrder,
      mergePreparationOrder,
      reference,
      handleDownloadManifest,
    ],
  );

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const pickedPreparationOrder = await pickPreparationOrder(reference);
        if (!pickedPreparationOrder) return;
        await mergePreparationOrder(pickedPreparationOrder);
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      } finally {
        setLoading(false);
      }
    })();
  }, [alert, reference, mergePreparationOrder, pickPreparationOrder]);

  return (
    <PageContainer
      title={
        <>
          Orden de preparación: <small>{reference}</small>
        </>
      }
      className="preparation-order-page"
    >
      <Dimmer active={loading} inverted>
        <Loader>Recuperando orden de preparación…</Loader>
      </Dimmer>
      <Dimmer active={isPrinting.isActive} inverted>
        <Loader
          {...(isPrinting.message === 'Imprimiendo etiqueta externa…' && {
            size: 'massive',
          })}
        >
          {isPrinting.message || 'Imprimiendo…'}
        </Loader>
      </Dimmer>
      <Dimmer active={isClosingPreparationOrder} inverted>
        <Loader>Cerrando orden de preparación…</Loader>
      </Dimmer>
      <Dimmer active={isPickingPreparationOrder} inverted>
        <Loader>
          Cambiando estado de la orden de preparación a "continuar preparando"
        </Loader>
      </Dimmer>
      <Dimmer active={isTransferPreparationOrder} inverted>
        <Loader>
          Cambiando estado de la orden de preparación a "pendiente de traspaso"
        </Loader>
      </Dimmer>
      <Dimmer active={requestingManifestDownload} inverted>
        <Loader>Descargando manifiesto y etiqueta de pallet</Loader>
      </Dimmer>
      <>
        <Card.Header>
          <Link to={`/preparation-orders`}>
            <Button
              basic
              circular
              content={
                <>
                  <Icon name="arrow left" />
                  Volver
                </>
              }
            />
          </Link>
          <div>
            {!!preparationOrder && (
              <>
                <Button as="a" href={preparationOrder?.file_url}>
                  Descargar Orden
                </Button>
                {userCanPrint && (
                  <>
                    <>
                      <Button
                        onClick={(event) => setConfirmTransferIsVisible(true)}
                      >
                        Marcar Traspaso
                      </Button>
                      <Confirm
                        open={confirmTransferIsVisible}
                        content="¿Confirmas el cambio de la orden a estado Pendiente de Traspaso? Esta acción es irreversible."
                        cancelButton={{
                          content: 'Cancelar',
                          autoFocus: true,
                        }}
                        confirmButton="Marcar como Pendiente de Traspaso"
                        onCancel={(event, data) =>
                          setConfirmTransferIsVisible(false)
                        }
                        onConfirm={handleConfirmTransfer}
                      />
                    </>
                    <Button
                      primary
                      loading={requestingManifestDownload}
                      disabled={
                        !!preparationOrder?.agency?.agency_code &&
                        (requestingManifestDownload || !palletInProgress)
                      }
                      onClick={(event) => {
                        handleDownloadManifest(
                          preparationOrder.agency.agency_code,
                          () => mergePreparationOrder(preparationOrder),
                        );
                      }}
                    >
                      Generar pallet
                    </Button>
                  </>
                )}
                {userIsStock && (
                  <>
                    <>
                      <Button
                        onClick={(event) => setConfirmPickingIsVisible(true)}
                      >
                        Continuar preparando
                      </Button>
                      <Confirm
                        open={confirmPickingIsVisible}
                        content="¿Confirmas el cambio de la orden a estado Continuar Preparando? Esta acción es irreversible."
                        cancelButton={{
                          content: 'Cancelar',
                          autoFocus: true,
                        }}
                        confirmButton="Marcar como Continuar Preparando"
                        onCancel={(event, data) =>
                          setConfirmPickingIsVisible(false)
                        }
                        onConfirm={handleConfirmPicking}
                      />
                    </>
                  </>
                )}
                {userIsStock && !userIsSFPStock && (
                  <>
                    <Button
                      primary
                      onClick={(event) => setConfirmCloseIsVisible(true)}
                    >
                      Cerrar orden
                    </Button>
                    <CriticalConfirmModal
                      content="¿Confirmas el cierre de la orden? Esta acción es irreversible."
                      confirmableText="cerrar de forma irreversible"
                      onConfirm={handleConfirmClose}
                      confirmButton="Cerrar orden de preparación"
                      confirmProps={{
                        open: confirmCloseIsVisible,
                        cancelButton: 'Cancelar',
                        onCancel: () => setConfirmCloseIsVisible(false),
                      }}
                    />
                  </>
                )}
                {userIsSFPStock && (
                  <ArticlesReplacerModal preparationOrder={preparationOrder} />
                )}
              </>
            )}
          </div>
        </Card.Header>
        <Card.Content>
          {!!preparationOrder ? (
            <>
              <WarehouseOrderPreparation
                userCanPrint={userCanPrint}
                setIsPrinting={setIsPrinting}
                preparationOrder={preparationOrder}
                setPreparationOrder={setPreparationOrder}
                updatePreparationOrder={updatePreparationOrder}
              />
              <PendingManifestKPI
                unmanifestedShipments={preparationOrder?.not_manifested}
                onManifestDownloaded={() =>
                  mergePreparationOrder(preparationOrder)
                }
              />
            </>
          ) : (
            <Message info>
              No se encuentra la orden de preparación {reference}
            </Message>
          )}
        </Card.Content>
      </>
    </PageContainer>
  );
}

export default withDashboardLayout(PreparationOrderPage);
